import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Rss } from "lucide-react";

import { AuthorAvatar } from "@/features/blog/components/author-avatar";
import type { BlogAuthor } from "@/features/blog/types";
import { blogPagePath } from "@/features/blog/utils/blog-head";
import { displayTitle, Eyebrow } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** The words that open a blog list, inside the head card: the blog's name, a title and a line under it. */
export const BlogHero = ({ eyebrow, title, lede }: { eyebrow?: string; title: string; lede?: string }) => (
  <div className="relative mx-auto max-w-[880px] px-1 pt-14 pb-4 text-center md:pt-20 md:pb-8">
    <Link to="/blog" className="inline-flex outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <Eyebrow>{eyebrow ?? m.blog_title()}</Eyebrow>
    </Link>
    <h1 className={cn(displayTitle, "mt-6")}>{title}</h1>
    {lede ? <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">{lede}</p> : null}
    <a
      href={blogPagePath("/rss.xml")}
      className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <Rss aria-hidden className="size-3.5" />
      RSS
    </a>
  </div>
);

const hostOf = (url: string) => {
  const host = URL.canParse(url) ? new URL(url).hostname.replace(/^www\./, "") : url;
  return host.split(".")[0] ?? host;
};

/** An author's page opens with them: their face, name, role, a few sentences and where else to find them. */
export const AuthorHero = ({ author }: { author: BlogAuthor }) => (
  <div className="relative mx-auto grid max-w-[720px] justify-items-center px-1 pt-14 pb-4 text-center md:pt-20 md:pb-8">
    <AuthorAvatar author={author} className="size-24 text-2xl shadow-[0_18px_40px_var(--site-glow)] ring-4 ring-background [&_svg]:h-9" />
    <Link to="/blog" className="mt-7 inline-flex outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <Eyebrow>{m.blog_title()}</Eyebrow>
    </Link>
    <h1 className="mt-4 text-[clamp(2.25rem,4.4vw,3.5rem)] leading-none font-medium tracking-[-0.045em]">{author.name}</h1>
    {author.role ? <p className="mt-3 text-[15px] font-medium text-[var(--site-ink)]">{author.role}</p> : null}
    {author.bio ? <p className="mt-5 max-w-[56ch] text-[17px] leading-relaxed text-pretty text-muted-foreground">{author.bio}</p> : null}
    {author.links.length ? (
      <ul className="mt-6 flex flex-wrap justify-center gap-2">
        {author.links.map((link) => (
          <li key={link}>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer me"
              className="inline-flex h-8 items-center gap-1 rounded-full border border-border bg-background px-3.5 text-[13px] font-medium capitalize transition-colors hover:bg-muted"
            >
              {hostOf(link)}
              <ArrowUpRight aria-hidden className="size-3.5 text-muted-foreground" />
            </a>
          </li>
        ))}
      </ul>
    ) : null}
  </div>
);
